import api from "@/api/api"
import { query, mutation } from "../../api/queries"
import { isPublicKey, toPublicKey } from "./publicFiles"

const LISTING_LIMIT = 1000
const MAX_LISTING_PAGES = 100
const ALREADY_EXISTS = /already exists/i

const errorText = (res, field) => {
	const result = res?.data?.[field]
	const messages = [
		...[].concat(result?.errors ?? []),
		...[].concat(result?.errors_2 ?? []).map(error => error?.message),
		...[].concat(res?.errors ?? []).map(error => error?.message),
	]
		.filter(Boolean)
		.map(message => String(message).trim())
	return messages.length ? messages.join("; ") : null
}

const findObject = async key => {
	for (let page = 0; page < MAX_LISTING_PAGES; page++) {
		const res = await api({
			query: query.fileListing,
			variables: { prefix: key, sort: "created_date", skip: page * LISTING_LIMIT, limit: LISTING_LIMIT },
		})
		const listing = res?.data?.fileListing
		if (!listing) throw new Error(errorText(res, "fileListing") ?? "The public folder could not be listed")
		const objects = listing.objectSummaries ?? []
		const found = objects.find(object => object.key === key)
		if (found) return found
		if (objects.length < LISTING_LIMIT) return null
	}
	throw new Error("Too many files share this name to check for a public copy")
}

const findSourceAndCopy = async (key, publicKey) => {
	const source = await findObject(key)
	return { source, copy: source ? await findObject(publicKey) : null }
}

export async function ensurePublicCopy(key) {
	if (isPublicKey(key)) return { ok: true }
	const publicKey = toPublicKey(key)
	try {
		const res = await api({
			query: mutation.copyFiles,
			variables: { copyFileInputs: [{ key, newKey: publicKey, isPublic: true }] },
		})
		if (res?.data?.copyFiles?.status === "success") return { ok: true }
		const error = errorText(res, "copyFiles")
		if (!ALREADY_EXISTS.test(error ?? "")) return { ok: false, error }
		const { source, copy } = await findSourceAndCopy(key, publicKey).catch(() => ({}))
		if (!copy) return { ok: false, error }
		if (copy.size === source.size) return { ok: true }
		return { ok: false, error: `A different file already exists at ${publicKey}` }
	} catch (error) {
		return { ok: false, error: error?.message ?? null }
	}
}

export async function removePublicCopy(key) {
	if (isPublicKey(key)) return { ok: true, removed: false }
	const publicKey = toPublicKey(key)
	try {
		if (!(await findObject(publicKey))) return { ok: true, removed: false }
		const res = await api({ query: mutation.deleteFile, variables: { key: publicKey } })
		if (res?.data?.deleteFile?.status === "success") return { ok: true, removed: true }
		return { ok: false, error: errorText(res, "deleteFile") }
	} catch (error) {
		return { ok: false, error: error?.message ?? null }
	}
}
